/**
 * The pieces the two roadmap boards are drawn out of: a column, and the three
 * kinds of card that go in one.
 *
 * They lived in `Roadmap.tsx` until that file reached 321 lines with two boards,
 * a move menu and three card layouts inside it, and the board was about to grow
 * an overdue counter, a hidden-rows line and a move receipt on top of that
 * (AGENTS.md §4). The split is by responsibility rather than by line count:
 * everything here is presentation that takes what to draw and a callback, and
 * `Roadmap.tsx` keeps every decision about what the board contains and what a
 * move writes back.
 *
 * Nothing in this file talks to the API. That is the seam: a card cannot start
 * a write on its own, so the two boards stay the only places where a click
 * turns into a PATCH.
 */
import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Check, CheckCircle2, Circle, EyeOff, Flag, ListTodo, MoveRight } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { type Milestone, type Project, type Task, type TaskStatus } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { StatusPill, fmtDate } from "../../ui";
import { Progress } from "./Progress";
import { ROADMAP_ORDER, isOverdue, roadmapColumn, type RoadmapCol } from "./lib";
import { TASK_STATUSES, columnTone, taskTone } from "./status";

type T = ReturnType<typeof useT>;

/** A card is wider than pill padding allows, so it takes the softened corners
 *  rather than the 999px ones - see `.crm-list-row-soft`. */
const CARD = "crm-prow crm-list-row-soft relative flex-col items-stretch gap-2.5 p-3.5";

/**
 * One column of the board.
 *
 * `overdue` arrives as an already-translated sentence rather than a number: this
 * file draws, it does not count, and the two boards count different things into
 * the same column (a project past its deadline, a task or milestone past its due
 * date). Handing over the finished string keeps that decision next to the data
 * it is made from. It sits beside the label rather than beside the count so the
 * count stays where the eye already looks for it, pinned right.
 */
export function Column({ tone, label, count, overdue, children }: {
  tone: RoadmapCol;
  label: string;
  count: number;
  overdue?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="crm-card flex w-[272px] shrink-0 flex-col gap-2.5 p-3.5">
      <div className="flex items-center gap-2 px-0.5 pb-0.5">
        <StatusPill tone={columnTone(tone)}>{label}</StatusPill>
        {overdue ? <span className="crm-tnum text-[11.5px] font-bold text-red-600">{overdue}</span> : null}
        <span className="crm-mut crm-tnum crm-push text-[12px] font-bold">{count}</span>
      </div>
      {children}
    </div>
  );
}

/** The "nothing here" line inside a column. Deliberately not an `EmptyState`:
 *  four illustrated empties side by side would shout louder than the work. */
export function ColumnEmpty({ label }: { label: string }) {
  return <p className="crm-mut px-1 py-5 text-center text-[12px]">{label}</p>;
}

/**
 * The move control on a card. A menu rather than a select for the same reason
 * `MenuField` exists: a native popup is drawn by the OS and lands on top of the
 * board as a grey system list. Needs a `PopHost` ancestor.
 *
 * Exported because the milestone board moves a card between PHASES with the same
 * control, and generic over the value for that reason: the two boards move a
 * card along different axes (a roadmap column, a milestone id) and neither axis
 * belongs in here. It is also the section's answer to dragging - the repo has
 * been bitten twice by pointer capture eating clicks - so the menu is the move,
 * not a fallback beside one, and it is keyboard-reachable because `Pop` gives
 * its rows roving focus.
 */
export function MoveMenu<V extends string>({ label, value, options, onPick }: {
  label: string;
  value: V | null;
  options: { value: V; label: string }[];
  onPick: (v: V) => void;
}) {
  const close = useClosePops();
  return (
    // Above the card-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] shrink-0">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb crm-cb-sm", open && "is-on")}
            title={label}
            aria-label={label}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoveRight className="size-3.5" />
          </button>
        )}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={cn("crm-mi", o.value === value && "is-on")}
            onClick={() => {
              onPick(o.value);
              close();
            }}
          >
            <span className="flex-1">{o.label}</span>
            {o.value === value ? <Check className="size-4" /> : null}
          </button>
        ))}
      </Pop>
    </span>
  );
}

/** A date under a card title, red once it has gone by. */
function DueLine({ iso, late }: { iso: string | null; late?: boolean }) {
  if (!iso) return null;
  return (
    <span className={cn("crm-tnum flex items-center gap-1.5 text-[11px]", late ? "text-red-600" : "crm-mut")}>
      <CalendarClock className="size-3 shrink-0" />
      {fmtDate(iso)}
    </span>
  );
}

/** One project on the portfolio board. */
export function ProjectCard({ t, p, onMove }: { t: T; p: Project; onMove: (p: Project, col: RoadmapCol) => void }) {
  const col = roadmapColumn(p);
  return (
    <div className={CARD}>
      <div className="flex items-start gap-2.5">
        <span className="min-w-0 flex-1">
          <Link
            to={`/projects/${p.id}`}
            className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-[22px]"
          >
            {p.name}
          </Link>
          {p.client?.name ? <span className="crm-prow-s crm-trunc block">{p.client.name}</span> : null}
        </span>
        <MoveMenu
          label={t("projects.roadmap.move")}
          value={col}
          options={ROADMAP_ORDER.map((c) => ({ value: c, label: t(`projects.roadmap.${c}`) }))}
          onPick={(c) => onMove(p, c)}
        />
      </div>

      {/* Effective first, stored as the fallback - the same rule the register's
          rows follow. The fallback is defence, not a state anybody watches: a
          moved card is written back through `patchRow`, which MERGES the PATCH
          response onto the row it already had, and the response simply has no
          `effectiveProgressPct` key to overwrite it with. So the field survives
          a move. What the fallback covers is a row that reaches this card from
          an endpoint which does not compute it at all - and there a stale number
          beats a blank bar, which reads as a failed load. */}
      <Progress pct={p.effectiveProgressPct ?? p.progressPct} />

      <div className="flex flex-wrap items-center gap-2">
        {p.status === "on_hold" ? <StatusPill tone="wt">{t("projects.roadmap.onHold")}</StatusPill> : null}
        {p.startDate ? (
          <DueLine iso={p.startDate} />
        ) : col === "future" ? (
          <span className="crm-mut text-[11px]">{t("projects.roadmap.noStart")}</span>
        ) : null}
      </div>
    </div>
  );
}

/** One milestone on the in-project board. The chip is what keeps it from
 *  reading as another task - see the note on `ProjectRoadmap`. */
export function MilestoneCard({ t, m, onToggle }: { t: T; m: Milestone; onToggle: (m: Milestone) => void }) {
  return (
    <div className={CARD}>
      <div className="flex items-start gap-2.5">
        <Flag className="mt-0.5 size-3.5 shrink-0 text-[var(--color-primary)]" />
        <span className="crm-prow-n min-w-0 flex-1">{m.name}</span>
        <button
          type="button"
          className="crm-cb crm-cb-sm shrink-0"
          onClick={() => onToggle(m)}
          title={t("projects.roadmap.completed")}
          aria-label={t("projects.roadmap.completed")}
          aria-pressed={m.done}
        >
          {m.done ? (
            <CheckCircle2 className="size-4 text-[var(--color-success)]" />
          ) : (
            <Circle className="size-4" />
          )}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="crm-chip">{t("projects.roadmap.milestoneLabel")}</span>
        <DueLine iso={m.dueDate} late={!m.done && !!m.dueDate && new Date(m.dueDate) < new Date()} />
      </div>
    </div>
  );
}

/** One task on the in-project board. */
export function TaskCard({ t, tk, onMove }: { t: T; tk: Task; onMove: (id: string, s: TaskStatus) => void }) {
  return (
    <div className={CARD}>
      <div className="flex items-start gap-2.5">
        <ListTodo className="crm-mut mt-0.5 size-3.5 shrink-0" />
        <span className="crm-prow-n min-w-0 flex-1">{tk.title}</span>
        {/* The same closed eye the two task lists draw. No overlay to sit above
            here - this card has no row-wide link - so it needs no z-index. */}
        {tk.visibleToClient ? null : (
          <span
            className="crm-mut mt-0.5 inline-flex shrink-0"
            role="img"
            title={t("projects.hiddenFromClient")}
            aria-label={t("projects.hiddenFromClient")}
          >
            <EyeOff className="size-3.5" />
          </span>
        )}
        <MoveMenu
          label={t("projects.roadmap.move")}
          value={tk.status}
          options={TASK_STATUSES.map((s) => ({ value: s, label: t(`tasks.status.${s}`) }))}
          onPick={(s) => onMove(tk.id, s)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={taskTone(tk.status)}>{t(`tasks.status.${tk.status}`)}</StatusPill>
        <DueLine iso={tk.dueDate} late={isOverdue(tk)} />
      </div>
    </div>
  );
}
